// ============================================
// IMPORTS
// ============================================
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { ApiV1ResponsePaginationMeta } from 'src/types/api.v1.response';
import type {
  DashboardOverview,
  PropertyTask,
  Workspace,
  Team,
  Project,
  TaskSchedule,
  WorkspaceStats,
  TeamStats,
  ProjectStats,
  ScheduleStats,
  ConversationStats,
  DashboardConversation,
} from 'src/types/api.v1.dashboard';
import type { PropertyResource } from 'src/types/api.v1.property';
import type { Contact } from 'src/types/api.v1.contact';
import type { PropertyCategory } from 'src/enums/api.v1.property.category';

// ============================================
// TYPES
// ============================================

/**
 * Property statistics
 */
export interface PropertyStats {
  total: number;
  created: number;
  owned: number;
  rented: number;
  service: number;
  shared: number;
  favorites: number;
}

/**
 * Task statistics
 */
export interface TaskStats {
  total: number;
  pending: number;
  in_progress: number;
  completed: number;
  overdue: number;
  urgent: number;
  high_priority: number;
  due_today: number;
  due_this_week: number;
}

/**
 * A single entry in the dashboard "recent updates" feed
 * (GET /v1/dashboard/activity). The signed-in user's own recent notes +
 * comments + notifications, merged and time-sorted server-side.
 * `is_read` is only meaningful for notifications (null for notes/comments).
 */
export interface ActivityItem {
  type: 'note' | 'comment' | 'notification';
  uuid: string;
  occurred_at: string | null;
  excerpt: string;
  is_read: boolean | null;
}

/** Dashboard time-tracking summary (GET /v1/dashboard/time). */
export interface TimeSummary {
  week_seconds: number;
  is_running: boolean;
}

// ============================================
// CONSTANTS
// ============================================

/**
 * Store identifier
 */
const STORE_ID = 'store-dashboard';

/**
 * Task statuses that indicate completion
 */
const COMPLETED_STATUSES = ['completed', 'cancelled'] as const;
type CompletedStatus = (typeof COMPLETED_STATUSES)[number];

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Check if status is a completed status
 */
function isCompletedStatus(status: string): status is CompletedStatus {
  return (COMPLETED_STATUSES as readonly string[]).includes(status);
}

/**
 * Check if task is overdue
 */
function isTaskOverdue(task: PropertyTask): boolean {
  if (!task.task?.due_date) return false;
  const now = new Date();
  return new Date(task.task.due_date) < now && !isCompletedStatus(task.task?.status ?? '');
}

// ============================================
// STORE DEFINITION
// ============================================

/**
 * Dashboard store for managing dashboard data and state
 */
export const useDashboardStore = defineStore(STORE_ID, () => {
  // ==========================================
  // STATE - Data
  // ==========================================

  const overview = ref<DashboardOverview | null>(null);
  const properties = ref<PropertyResource[]>([]);
  const propertiesPagination = ref<ApiV1ResponsePaginationMeta | null>(null);
  const propertiesStats = ref<PropertyStats | null>(null);
  const tasks = ref<PropertyTask[]>([]);
  const tasksPagination = ref<ApiV1ResponsePaginationMeta | null>(null);
  const tasksStats = ref<TaskStats | null>(null);
  const workspaces = ref<Workspace[]>([]);
  const workspacesStats = ref<WorkspaceStats | null>(null);
  const teams = ref<Team[]>([]);
  const teamsStats = ref<TeamStats | null>(null);
  const projects = ref<Project[]>([]);
  const projectsStats = ref<ProjectStats | null>(null);
  const schedules = ref<TaskSchedule[]>([]);
  const schedulesStats = ref<ScheduleStats | null>(null);
  const conversations = ref<DashboardConversation[]>([]);
  const conversationsStats = ref<ConversationStats | null>(null);
  const timeSummary = ref<TimeSummary | null>(null);
  const recentContacts = ref<Contact[]>([]);
  const recentActivity = ref<ActivityItem[]>([]);

  // ==========================================
  // STATE - Loading
  // ==========================================

  const isLoading = ref(false);
  const isPropertiesLoading = ref(false);
  const isPropertiesStatsLoading = ref(false);
  const isTasksLoading = ref(false);
  const isTasksStatsLoading = ref(false);
  const isWorkspacesLoading = ref(false);
  const isTeamsLoading = ref(false);
  const isProjectsLoading = ref(false);
  const isSchedulesLoading = ref(false);
  const isConversationsLoading = ref(false);
  // The time slice needs a real flag: its loading state used to be inferred
  // from `timeSummary === null`, which never resolved after a failed fetch and
  // pinned the "Hours this week" tile in its skeleton forever.
  const isTimeLoading = ref(false);

  // ==========================================
  // STATE - Errors
  // ==========================================

  const error = ref<string | null>(null);
  const activityError = ref<string | null>(null);
  const propertiesError = ref<string | null>(null);
  const propertiesStatsError = ref<string | null>(null);
  const tasksError = ref<string | null>(null);
  const tasksStatsError = ref<string | null>(null);
  const workspacesError = ref<string | null>(null);
  const teamsError = ref<string | null>(null);
  const projectsError = ref<string | null>(null);
  const schedulesError = ref<string | null>(null);
  const conversationsError = ref<string | null>(null);
  const timeError = ref<string | null>(null);
  const contactsError = ref<string | null>(null);

  // ==========================================
  // STATE - Freshness Tracking
  // ==========================================

  const lastFetched = ref<number | null>(null);
  // Fetch timestamps. `propertiesLastFetched` is load-bearing — it backs
  // `isPropertiesFetched`, which FirstRunWelcomeCard uses to tell "no properties
  // yet" from "not loaded yet". The rest are recorded but not read by anything:
  // they used to feed an `are*Stale` getter family that NO caller ever consulted
  // (verified 2026-08-17), so the 5-minute cache this store appeared to implement
  // never skipped a single request. Kept as the substrate for wiring a real TTL
  // (plan item 3.1) rather than deleted, but do not mistake them for a cache.
  const propertiesLastFetched = ref<number | null>(null);
  const propertiesStatsLastFetched = ref<number | null>(null);
  const tasksLastFetched = ref<number | null>(null);
  const tasksStatsLastFetched = ref<number | null>(null);
  const workspacesLastFetched = ref<number | null>(null);
  const teamsLastFetched = ref<number | null>(null);
  const projectsLastFetched = ref<number | null>(null);
  const schedulesLastFetched = ref<number | null>(null);
  const conversationsLastFetched = ref<number | null>(null);

  // Two separate questions, deliberately two refs.
  //
  // `dashboardLastFetchedAt` is FRESHNESS: when the last full fetchDashboard()
  // completed. It drives stale-while-revalidate, and a mutation elsewhere in the
  // app clears it so the next visit revalidates.
  //
  // `hasLoadedDashboardOnce` is HAVING DATA: has a full load ever landed in this
  // session. It survives invalidation, because dropping freshness must not put a
  // populated page back behind skeletons. Only clearData() (logout) resets it.
  const dashboardLastFetchedAt = ref<number | null>(null);
  const hasLoadedDashboardOnce = ref(false);

  // ==========================================
  // GETTERS - Data
  // ==========================================

  const overviewData = computed(() => overview.value);
  const propertiesData = computed(() => properties.value);
  const propertiesStatsData = computed(() => propertiesStats.value);
  const tasksData = computed(() => tasks.value);
  const tasksStatsData = computed(() => tasksStats.value);
  const propertiesPaginationData = computed(() => propertiesPagination.value);
  const tasksPaginationData = computed(() => tasksPagination.value);
  const workspacesData = computed(() => workspaces.value);
  const workspacesStatsData = computed(() => workspacesStats.value);
  const teamsData = computed(() => teams.value);
  const teamsStatsData = computed(() => teamsStats.value);
  const projectsData = computed(() => projects.value);
  const projectsStatsData = computed(() => projectsStats.value);
  const schedulesData = computed(() => schedules.value);
  const schedulesStatsData = computed(() => schedulesStats.value);
  const conversationsData = computed(() => conversations.value);
  const timeSummaryData = computed(() => timeSummary.value);
  const recentContactsData = computed(() => recentContacts.value);
  const recentActivityData = computed(() => recentActivity.value);
  const conversationsStatsData = computed(() => conversationsStats.value);

  // ==========================================
  // GETTERS - Loading State
  // ==========================================
  // Properties loading state
  const arePropertiesLoading = computed(() => isPropertiesLoading.value);
  // PropertiesStats loading state
  const arePropertiesStatsLoading = computed(() => isPropertiesLoading.value);
  // Tasks loading state
  const areTasksLoading = computed(() => isTasksLoading.value);
  // TasksStats loading state
  const areTasksStatsLoading = computed(() => isTasksLoading.value);
  // Workspaces loading state
  const areWorkspacesLoading = computed(() => isWorkspacesLoading.value);
  // Teams loading state
  const areTeamsLoading = computed(() => isTeamsLoading.value);
  // Projects loading state
  const areProjectsLoading = computed(() => isProjectsLoading.value);
  // Schedules loading state
  const areSchedulesLoading = computed(() => isSchedulesLoading.value);
  // Conversations loading state
  const areConversationsLoading = computed(() => isConversationsLoading.value);
  // Time-summary loading state
  const isTimeSummaryLoading = computed(() => isTimeLoading.value);
  const isAnyLoading = computed(
    () =>
      isLoading.value ||
      isPropertiesLoading.value ||
      isPropertiesStatsLoading.value ||
      isTasksLoading.value ||
      isTasksStatsLoading.value ||
      isWorkspacesLoading.value ||
      isTeamsLoading.value ||
      isProjectsLoading.value ||
      isSchedulesLoading.value ||
      isConversationsLoading.value ||
      isTimeLoading.value,
  );

  // ==========================================
  // GETTERS - Error State
  // ==========================================

  const overviewError = computed(() => error.value);
  const propertiesErrorMessage = computed(() => propertiesError.value);
  const propertiesStatsErrorMessage = computed(() => propertiesStatsError.value);
  const tasksErrorMessage = computed(() => tasksError.value);
  const tasksStatsErrorMessage = computed(() => tasksStatsError.value);
  const workspacesErrorMessage = computed(() => workspacesError.value);
  const teamsErrorMessage = computed(() => teamsError.value);
  const projectsErrorMessage = computed(() => projectsError.value);
  const schedulesErrorMessage = computed(() => schedulesError.value);
  const conversationsErrorMessage = computed(() => conversationsError.value);
  const timeErrorMessage = computed(() => timeError.value);
  const contactsErrorMessage = computed(() => contactsError.value);
  const activityErrorMessage = computed(() => activityError.value);
  const hasErrors = computed(
    () =>
      !!error.value ||
      !!timeError.value ||
      !!propertiesError.value ||
      !!propertiesStatsError.value ||
      !!tasksError.value ||
      !!tasksStatsError.value ||
      !!workspacesError.value ||
      !!teamsError.value ||
      !!projectsError.value ||
      !!schedulesError.value ||
      !!conversationsError.value,
  );

  // ==========================================
  // GETTERS - Data State
  // ==========================================

  const isOverviewLoaded = computed(() => !!overview.value);
  const arePropertiesLoaded = computed(() => properties.value.length > 0);
  // True once the first /properties fetch has resolved. Distinct from
  // arePropertiesLoaded (which is `length > 0` — a 'has data' signal, not
  // a 'fetch ran' signal). Consumers that distinguish "empty because not
  // loaded yet" vs "empty because the user has zero properties" use this
  // — see FirstRunWelcomeCard.shouldShow.
  const isPropertiesFetched = computed(() => propertiesLastFetched.value !== null);
  const hasLoadedDashboardData = computed(() => hasLoadedDashboardOnce.value);
  const dashboardFetchedAt = computed(() => dashboardLastFetchedAt.value);
  const arePropertiesStatsLoaded = computed(() => !!propertiesStats.value);
  const areTasksLoaded = computed(() => tasks.value.length > 0);
  const areTasksStatsLoaded = computed(() => !!tasksStats.value);
  const areWorkspacesLoaded = computed(() => workspaces.value.length > 0);
  const areTeamsLoaded = computed(() => teams.value.length > 0);
  const areProjectsLoaded = computed(() => projects.value.length > 0);
  const areSchedulesLoaded = computed(() => schedules.value.length > 0);
  const areConversationsLoaded = computed(() => conversations.value.length > 0);

  // ==========================================
  // GETTERS - Counts
  // ==========================================

  const propertiesCount = computed(() => properties.value.length);
  const tasksCount = computed(() => tasks.value.length);
  const favoritePropertiesCount = computed(
    () => properties.value.filter((p) => p.property_assignable?.is_favorite === true).length,
  );
  const pendingTasksCount = computed(
    () => tasks.value.filter((t) => t.task?.status === 'pending').length,
  );
  const overdueTasksCount = computed(() => tasks.value.filter(isTaskOverdue).length);
  const unreadConversationsCount = computed(
    () => conversations.value.filter((c) => c.unread_count > 0).length,
  );
  const totalUnreadMessagesCount = computed(
    () => conversations.value.reduce((sum, c) => sum + c.unread_count, 0),
  );

  // ==========================================
  // GETTERS - Filtered Data
  // ==========================================

  const favoriteProperties = computed(() =>
    properties.value.filter((p) => p.property_assignable?.is_favorite === true),
  );

  const overdueTasks = computed(() => tasks.value.filter(isTaskOverdue));

  // ==========================================
  // ACTIONS - Overview
  // ==========================================

  /** Record that a full dashboard fetch completed. */
  function markDashboardFetched(): void {
    dashboardLastFetchedAt.value = Date.now();
    hasLoadedDashboardOnce.value = true;
  }

  /**
   * Drop the cached snapshot's freshness without dropping the data.
   *
   * Called whenever a resource the dashboard summarises is created, updated or
   * deleted. The page keeps showing what it has (no blank flash) but the next
   * visit revalidates instead of trusting the TTL — otherwise creating your
   * first property and going straight to the dashboard would still show zero
   * properties and the "add your first property" hero.
   */
  function invalidateDashboard(): void {
    dashboardLastFetchedAt.value = null;
  }

  /**
   * Is the cached dashboard older than `ttlMs`? Never-fetched counts as stale.
   */
  function isDashboardStale(ttlMs: number): boolean {
    if (dashboardLastFetchedAt.value === null) return true;
    return Date.now() - dashboardLastFetchedAt.value > ttlMs;
  }

  /**
   * Set overview data
   */
  function setOverview(data: DashboardOverview): void {
    overview.value = data;
    lastFetched.value = Date.now();
    error.value = null;
  }

  /**
   * Clear overview data
   */
  function clearOverview(): void {
    overview.value = null;
    lastFetched.value = null;
    error.value = null;
  }

  // ==========================================
  // ACTIONS - Properties
  // ==========================================

  /**
   * Set properties data
   */
  function setProperties(data: PropertyResource[]): void {
    properties.value = data;
    propertiesLastFetched.value = Date.now();
    propertiesError.value = null;
  }

  /**
   * Add properties to existing list
   */
  function addProperties(data: PropertyResource[]): void {
    properties.value.push(...data);
    propertiesLastFetched.value = Date.now();
    propertiesError.value = null;
  }

  /**
   * Update a specific property
   */
  function updateProperty(propertyUuid: string, updates: Partial<PropertyResource>): void {
    const index = properties.value.findIndex((p) => p.uuid === propertyUuid);
    if (index !== -1) {
      properties.value[index] = {
        ...properties.value[index]!,
        ...updates,
      } as PropertyResource;
    }
  }

  /**
   * Remove a property
   */
  function removeProperty(propertyUuid: string): void {
    const index = properties.value.findIndex((p) => p.uuid === propertyUuid);
    if (index !== -1) {
      properties.value.splice(index, 1);
    }
  }

  /**
   * Set properties pagination
   */
  function setPropertiesPagination(pagination: ApiV1ResponsePaginationMeta): void {
    propertiesPagination.value = pagination;
  }

  /**
   * Clear properties data
   */
  function clearProperties(): void {
    properties.value = [];
    propertiesPagination.value = null;
    propertiesLastFetched.value = null;
    propertiesError.value = null;
  }

  // ==========================================
  // ACTIONS - Properties Statistics
  // ==========================================

  /**
   * Set properties statistics data
   */
  function setPropertiesStats(data: PropertyStats): void {
    propertiesStats.value = data;
    propertiesStatsLastFetched.value = Date.now();
    propertiesStatsError.value = null;
  }

  /**
   * Clear properties statistics data
   */
  function clearPropertiesStats(): void {
    propertiesStats.value = null;
    propertiesStatsLastFetched.value = null;
    propertiesStatsError.value = null;
  }

  // ==========================================
  // ACTIONS - Tasks
  // ==========================================

  /**
   * Set tasks data
   */
  function setTasks(data: PropertyTask[]): void {
    tasks.value = data;
    tasksLastFetched.value = Date.now();
    tasksError.value = null;
  }

  /**
   * Add tasks to existing list
   */
  function addTasks(data: PropertyTask[]): void {
    tasks.value.push(...data);
    tasksLastFetched.value = Date.now();
    tasksError.value = null;
  }

  /**
   * Update a specific task
   */
  function updateTask(taskUuid: string, updates: Partial<PropertyTask>): void {
    const index = tasks.value.findIndex((t) => t.uuid === taskUuid);
    if (index !== -1) {
      tasks.value[index] = {
        ...tasks.value[index]!,
        ...updates,
      } as PropertyTask;
    }
  }

  /**
   * Remove a task
   */
  function removeTask(taskUuid: string): void {
    const index = tasks.value.findIndex((t) => t.uuid === taskUuid);
    if (index !== -1) {
      tasks.value.splice(index, 1);
    }
  }

  /**
   * Set tasks pagination
   */
  function setTasksPagination(pagination: ApiV1ResponsePaginationMeta): void {
    tasksPagination.value = pagination;
  }

  /**
   * Clear tasks data
   */
  function clearTasks(): void {
    tasks.value = [];
    tasksPagination.value = null;
    tasksLastFetched.value = null;
    tasksError.value = null;
  }

  // ==========================================
  // ACTIONS - Tasks Statistics
  // ==========================================

  /**
   * Set tasks statistics data
   */
  function setTasksStats(data: TaskStats): void {
    tasksStats.value = data;
    tasksStatsLastFetched.value = Date.now();
    tasksStatsError.value = null;
  }

  /**
   * Clear tasks statistics data
   */
  function clearTasksStats(): void {
    tasksStats.value = null;
    tasksStatsLastFetched.value = null;
    tasksStatsError.value = null;
  }

  // ==========================================
  // ACTIONS - Workspaces
  // ==========================================

  /**
   * Set workspaces data
   */
  function setWorkspaces(data: Workspace[]): void {
    workspaces.value = data;
    workspacesLastFetched.value = Date.now();
    workspacesError.value = null;
  }

  /**
   * Set workspaces statistics data
   */
  function setWorkspacesStats(data: WorkspaceStats): void {
    workspacesStats.value = data;
    workspacesError.value = null;
  }

  /**
   * Clear workspaces data
   */
  function clearWorkspaces(): void {
    workspaces.value = [];
    workspacesStats.value = null;
    workspacesLastFetched.value = null;
    workspacesError.value = null;
  }

  // ==========================================
  // ACTIONS - Teams
  // ==========================================

  /**
   * Set teams data
   */
  function setTeams(data: Team[]): void {
    teams.value = data;
    teamsLastFetched.value = Date.now();
    teamsError.value = null;
  }

  /**
   * Set teams statistics data
   */
  function setTeamsStats(data: TeamStats): void {
    teamsStats.value = data;
    teamsError.value = null;
  }

  /**
   * Clear teams data
   */
  function clearTeams(): void {
    teams.value = [];
    teamsStats.value = null;
    teamsLastFetched.value = null;
    teamsError.value = null;
  }

  // ==========================================
  // ACTIONS - Projects
  // ==========================================

  /**
   * Set projects data
   */
  function setProjects(data: Project[]): void {
    projects.value = data;
    projectsLastFetched.value = Date.now();
    projectsError.value = null;
  }

  /**
   * Set projects statistics data
   */
  function setProjectsStats(data: ProjectStats): void {
    projectsStats.value = data;
    projectsError.value = null;
  }

  /**
   * Clear projects data
   */
  function clearProjects(): void {
    projects.value = [];
    projectsStats.value = null;
    projectsLastFetched.value = null;
    projectsError.value = null;
  }

  // ==========================================
  // ACTIONS - Schedules
  // ==========================================

  /**
   * Set schedules data
   */
  function setSchedules(data: TaskSchedule[]): void {
    schedules.value = data;
    schedulesLastFetched.value = Date.now();
    schedulesError.value = null;
  }

  /**
   * Set schedules statistics data
   */
  function setSchedulesStats(data: ScheduleStats): void {
    schedulesStats.value = data;
    schedulesError.value = null;
  }

  /**
   * Clear schedules data
   */
  function clearSchedules(): void {
    schedules.value = [];
    schedulesStats.value = null;
    schedulesLastFetched.value = null;
    schedulesError.value = null;
  }

  // ==========================================
  // ACTIONS - Conversations
  // ==========================================

  /**
   * Set conversations data
   */
  function setConversations(data: DashboardConversation[]): void {
    conversations.value = data;
    conversationsLastFetched.value = Date.now();
    conversationsError.value = null;
  }

  /**
   * Drop a conversation the user has stopped being a participant of.
   *
   * The messages page and the dashboard panel read the same array, so removing
   * it here clears both at once. Called when `conversation.access_revoked`
   * arrives: the thread is gone from under the user, and leaving it in the list
   * means the next click is a 403.
   */
  function removeConversation(uuid: string): void {
    const index = conversations.value.findIndex((conversation) => conversation.uuid === uuid);
    if (index !== -1) {
      conversations.value.splice(index, 1);
    }
  }

  /** Set the time-tracking summary slice. */
  function setTimeSummary(data: TimeSummary | null): void {
    timeSummary.value = data;
    timeError.value = null;
  }

  /** Set the time-tracking slice error. */
  function setTimeError(errorMessage: string | null): void {
    timeError.value = errorMessage;
  }

  /** Set the time-tracking slice loading state. */
  function setTimeLoading(loading: boolean): void {
    isTimeLoading.value = loading;
  }

  /** Set the recent-contacts slice. */
  function setRecentContacts(data: Contact[]): void {
    recentContacts.value = data;
    contactsError.value = null;
  }

  /** Set the recent-contacts slice error. */
  function setContactsError(errorMessage: string | null): void {
    contactsError.value = errorMessage;
  }

  /** Set the recent-activity ("recent updates") slice. */
  function setRecentActivity(data: ActivityItem[]): void {
    recentActivity.value = data;
    activityError.value = null;
  }

  /** Set the recent-activity slice error. */
  function setActivityError(errorMessage: string | null): void {
    activityError.value = errorMessage;
  }

  /**
   * Set conversations statistics data
   */
  function setConversationsStats(data: ConversationStats): void {
    conversationsStats.value = data;
    conversationsError.value = null;
  }

  /**
   * Clear conversations data
   */
  function clearConversations(): void {
    conversations.value = [];
    conversationsStats.value = null;
    conversationsLastFetched.value = null;
    conversationsError.value = null;
  }

  // ==========================================
  // ACTIONS - Loading State
  // ==========================================

  /**
   * Set overview loading state
   */
  function setLoading(loading: boolean): void {
    isLoading.value = loading;
  }

  /**
   * Set properties loading state
   */
  function setPropertiesLoading(loading: boolean): void {
    isPropertiesLoading.value = loading;
  }

  /**
   * Set properties statistics loading state
   */
  function setPropertiesStatsLoading(loading: boolean): void {
    isPropertiesStatsLoading.value = loading;
  }

  /**
   * Set tasks loading state
   */
  function setTasksLoading(loading: boolean): void {
    isTasksLoading.value = loading;
  }

  /**
   * Set tasks statistics loading state
   */
  function setTasksStatsLoading(loading: boolean): void {
    isTasksStatsLoading.value = loading;
  }

  /**
   * Set workspaces loading state
   */
  function setWorkspacesLoading(loading: boolean): void {
    isWorkspacesLoading.value = loading;
  }

  /**
   * Set teams loading state
   */
  function setTeamsLoading(loading: boolean): void {
    isTeamsLoading.value = loading;
  }

  /**
   * Set projects loading state
   */
  function setProjectsLoading(loading: boolean): void {
    isProjectsLoading.value = loading;
  }

  /**
   * Set schedules loading state
   */
  function setSchedulesLoading(loading: boolean): void {
    isSchedulesLoading.value = loading;
  }

  /**
   * Set conversations loading state
   */
  function setConversationsLoading(loading: boolean): void {
    isConversationsLoading.value = loading;
  }

  /**
   * Clear all loading states
   */
  function clearLoading(): void {
    isLoading.value = false;
    isPropertiesLoading.value = false;
    isPropertiesStatsLoading.value = false;
    isTasksLoading.value = false;
    isTasksStatsLoading.value = false;
    isWorkspacesLoading.value = false;
    isTeamsLoading.value = false;
    isProjectsLoading.value = false;
    isSchedulesLoading.value = false;
    isConversationsLoading.value = false;
    isTimeLoading.value = false;
  }

  // ==========================================
  // ACTIONS - Error State
  // ==========================================

  /**
   * Set overview error
   */
  function setError(errorMessage: string | null): void {
    error.value = errorMessage;
  }

  /**
   * Set properties error
   */
  function setPropertiesError(errorMessage: string | null): void {
    propertiesError.value = errorMessage;
  }

  /**
   * Set properties statistics error
   */
  function setPropertiesStatsError(errorMessage: string | null): void {
    propertiesStatsError.value = errorMessage;
  }

  /**
   * Set tasks error
   */
  function setTasksError(errorMessage: string | null): void {
    tasksError.value = errorMessage;
  }

  /**
   * Set tasks statistics error
   */
  function setTasksStatsError(errorMessage: string | null): void {
    tasksStatsError.value = errorMessage;
  }

  /**
   * Set workspaces error
   */
  function setWorkspacesError(errorMessage: string | null): void {
    workspacesError.value = errorMessage;
  }

  /**
   * Set teams error
   */
  function setTeamsError(errorMessage: string | null): void {
    teamsError.value = errorMessage;
  }

  /**
   * Set projects error
   */
  function setProjectsError(errorMessage: string | null): void {
    projectsError.value = errorMessage;
  }

  /**
   * Set schedules error
   */
  function setSchedulesError(errorMessage: string | null): void {
    schedulesError.value = errorMessage;
  }

  /**
   * Set conversations error
   */
  function setConversationsError(errorMessage: string | null): void {
    conversationsError.value = errorMessage;
  }

  /**
   * Clear all errors
   */
  function clearErrors(): void {
    error.value = null;
    activityError.value = null;
    timeError.value = null;
    contactsError.value = null;
    propertiesError.value = null;
    propertiesStatsError.value = null;
    tasksError.value = null;
    tasksStatsError.value = null;
    workspacesError.value = null;
    teamsError.value = null;
    projectsError.value = null;
    schedulesError.value = null;
    conversationsError.value = null;
  }

  // ==========================================
  // ACTIONS - Utility
  // ==========================================

  /**
   * Find property by UUID
   */
  function findPropertyByUuid(uuid: string): PropertyResource | undefined {
    return properties.value.find((p) => p.uuid === uuid);
  }

  /**
   * Find task by UUID
   */
  function findTaskByUuid(uuid: string): PropertyTask | undefined {
    return tasks.value.find((t) => t.uuid === uuid);
  }

  /**
   * Get tasks for a specific property
   */
  function getTasksByProperty(propertyUuid: string): PropertyTask[] {
    return tasks.value.filter((t) => t.property?.uuid === propertyUuid);
  }

  /**
   * Get properties by category
   */
  function getPropertiesByCategory(category: PropertyCategory): PropertyResource[] {
    // Compare API string to enum value
    return properties.value.filter((p) => p.property.category === String(category));
  }

  /**
   * Get tasks by status
   */
  function getTasksByStatus(status: string): PropertyTask[] {
    return tasks.value.filter((t) => t.task?.status === status);
  }

  /**
   * Get tasks by priority
   */
  function getTasksByPriority(priority: string): PropertyTask[] {
    return tasks.value.filter((t) => t.task?.priority === priority);
  }

  /**
   * Clear all data
   */
  function clearData(): void {
    clearOverview();
    clearProperties();
    clearPropertiesStats();
    clearTasks();
    clearTasksStats();
    clearWorkspaces();
    clearTeams();
    clearProjects();
    clearSchedules();
    clearConversations();
    timeSummary.value = null;
    timeError.value = null;
    recentContacts.value = [];
    contactsError.value = null;
    recentActivity.value = [];
    activityError.value = null;
    // Freshness must reset with the data, or the next user in this tab would
    // look cached-and-fresh with an empty store. clearUserScopedState() calls
    // this on both logout paths.
    dashboardLastFetchedAt.value = null;
    hasLoadedDashboardOnce.value = false;
    clearErrors();
    clearLoading();
  }

  /**
   * Reset store to initial state
   */
  function reset(): void {
    clearData();
  }

  // ==========================================
  // RETURN PUBLIC API
  // ==========================================

  return {
    // Data
    overviewData,
    propertiesData,
    propertiesStatsData,
    tasksData,
    tasksStatsData,
    propertiesPaginationData,
    tasksPaginationData,
    workspacesData,
    workspacesStatsData,
    teamsData,
    teamsStatsData,
    projectsData,
    projectsStatsData,
    schedulesData,
    schedulesStatsData,
    conversationsData,
    removeConversation,
    timeSummaryData,
    recentContactsData,
    recentActivityData,
    conversationsStatsData,

    // Loading State
    arePropertiesLoading,
    arePropertiesStatsLoading,
    areTasksLoading,
    areTasksStatsLoading,
    areWorkspacesLoading,
    areTeamsLoading,
    areProjectsLoading,
    areSchedulesLoading,
    areConversationsLoading,
    isTimeSummaryLoading,
    isAnyLoading,

    // Error State
    overviewError,
    propertiesErrorMessage,
    propertiesStatsErrorMessage,
    tasksErrorMessage,
    tasksStatsErrorMessage,
    workspacesErrorMessage,
    teamsErrorMessage,
    projectsErrorMessage,
    schedulesErrorMessage,
    conversationsErrorMessage,
    timeErrorMessage,
    contactsErrorMessage,
    activityErrorMessage,
    hasErrors,

    // Data State
    isOverviewLoaded,
    arePropertiesLoaded,
    isPropertiesFetched,
    hasLoadedDashboardData,
    dashboardFetchedAt,
    arePropertiesStatsLoaded,
    areTasksLoaded,
    areTasksStatsLoaded,
    areWorkspacesLoaded,
    areTeamsLoaded,
    areProjectsLoaded,
    areSchedulesLoaded,
    areConversationsLoaded,

    // Counts
    propertiesCount,
    tasksCount,
    favoritePropertiesCount,
    pendingTasksCount,
    overdueTasksCount,
    unreadConversationsCount,
    totalUnreadMessagesCount,

    // Filtered Data
    favoriteProperties,
    overdueTasks,

    // Overview Actions
    setOverview,
    clearOverview,

    // Properties Actions
    setProperties,
    addProperties,
    updateProperty,
    removeProperty,
    setPropertiesPagination,
    clearProperties,

    // Properties Statistics Actions
    setPropertiesStats,
    clearPropertiesStats,

    // Tasks Actions
    setTasks,
    addTasks,
    updateTask,
    removeTask,
    setTasksPagination,
    clearTasks,

    // Tasks Statistics Actions
    setTasksStats,
    clearTasksStats,

    // Workspaces Actions
    setWorkspaces,
    setWorkspacesStats,
    clearWorkspaces,

    // Teams Actions
    setTeams,
    setTeamsStats,
    clearTeams,

    // Projects Actions
    setProjects,
    setProjectsStats,
    clearProjects,

    // Schedules Actions
    setSchedules,
    setSchedulesStats,
    clearSchedules,

    // Conversations Actions
    setConversations,
    setTimeSummary,
    setTimeError,
    setTimeLoading,
    markDashboardFetched,
    invalidateDashboard,
    isDashboardStale,
    setRecentContacts,
    setContactsError,
    setRecentActivity,
    setActivityError,
    setConversationsStats,
    clearConversations,

    // Loading Actions
    setLoading,
    setPropertiesLoading,
    setPropertiesStatsLoading,
    setTasksLoading,
    setTasksStatsLoading,
    setWorkspacesLoading,
    setTeamsLoading,
    setProjectsLoading,
    setSchedulesLoading,
    setConversationsLoading,
    clearLoading,

    // Error Actions
    setError,
    setPropertiesError,
    setPropertiesStatsError,
    setTasksError,
    setTasksStatsError,
    setWorkspacesError,
    setTeamsError,
    setProjectsError,
    setSchedulesError,
    setConversationsError,
    clearErrors,

    // Utility Actions
    findPropertyByUuid,
    findTaskByUuid,
    getTasksByProperty,
    getPropertiesByCategory,
    getTasksByStatus,
    getTasksByPriority,
    clearData,
    reset,
  };
});

// ============================================
// TYPE EXPORTS
// ============================================
export type DashboardStore = ReturnType<typeof useDashboardStore>;
